import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { BarFill, BarTrack } from 'pages/HrDashboard/hrLayout.styled'
import { IOpsLossPareto } from 'types/opsAnalytics.interface'
import { USE_OPS_MOCKS } from '../Shared/opsControl'
import { OPS_LOSS_PARETO, OPS_SHIFT_REVIEW } from '../Shared/opsAnalyticsData'
import { LOSS_BUCKET_META } from '../Shared/lossClassifier'
import { DemoBadge } from '../Shared/OpsSharedComponents'
import { LossWaterfall } from './LossWaterfall'
import { DefinitionHint, FootnoteWrap, HoursCellWrap, SectionStack } from './Losses.styled'

const PARETO_ROWS = [...OPS_LOSS_PARETO]
  .sort((a, b) => b.hours - a.hours)
  .map((row, index) => ({ ...row, id: index + 1 }))

const MAX_PARETO_HOURS = Math.max(...PARETO_ROWS.map((row) => row.hours), 1)

const paretoColumns: IHonestTableColumn<IOpsLossPareto & { id: number }>[] = [
  {
    id: 'bucket',
    header: 'Bucket',
    cell: (item) => (
      <DefinitionHint title={LOSS_BUCKET_META[item.bucket].definition}>
        <StatusPill status={LOSS_BUCKET_META[item.bucket].tone}>
          {LOSS_BUCKET_META[item.bucket].label}
        </StatusPill>
      </DefinitionHint>
    ),
  },
  {
    id: 'hours',
    header: 'Hours',
    minWidth: '14rem',
    cell: (item) => (
      <HoursCellWrap>
        <BodyText weight='700'>{item.hours} h</BodyText>
        <BarTrack>
          <BarFill
            $pct={(item.hours / MAX_PARETO_HOURS) * 100}
            $color={tokens.status[LOSS_BUCKET_META[item.bucket].tone].fg}
          />
        </BarTrack>
      </HoursCellWrap>
    ),
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: (item) =>
      item.costGhs != null ? (
        <BodyText>GH₵ {item.costGhs.toLocaleString('en-US')}</BodyText>
      ) : (
        <OtherText grey>—</OtherText>
      ),
  },
  {
    id: 'topContributor',
    header: 'Top contributor',
    cell: (item) => <BodyText>{item.topContributor}</BodyText>,
  },
]

export const Losses = () => {
  if (!USE_OPS_MOCKS) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Losses
          </Head1>
          <NoData text='Loss accounting starts once shift aggregates are computed at shift close.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem'>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Losses
          </Head1>
          <DemoBadge />
        </Flex>
        <Flex mt='4'>
          <BodyText grey>Every lost tonne attributed to a cause — tap a bar to see the evidence.</BodyText>
        </Flex>
        <SectionStack>
          <Container>
            <ContainerHeader>
              <TitleText>Yesterday’s shift</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <LossWaterfall bars={OPS_SHIFT_REVIEW.waterfall} />
            </ContainerContent>
          </Container>
          <Container>
            <ContainerHeader>
              <TitleText>Where the hours go (30 days)</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <HonestTable columns={paretoColumns} data={PARETO_ROWS} keyField='id' />
              <FootnoteWrap>
                <OtherText grey>
                  Costing: breakdown hours × machine rate — definitions in each tooltip.
                </OtherText>
              </FootnoteWrap>
            </ContainerContent>
          </Container>
        </SectionStack>
      </PageWrapper>
    </>
  )
}
